import { isBlackOpening, loadedBlackCourse } from "../content/blackCourses.ts";
import { structurePlan } from "../content/black/plans.ts";
import { Chess } from "@vendor/chess.js";
import type { ChessMove } from "../chess/chessTypes.ts";
import {
  bestCapture,
  captureGain,
  hasMateInOne,
  moveSafety,
  threatAgainstMover,
  winningCaptures,
  type Capture,
} from "../chess/exchange.ts";
import type { BoundaryGuide } from "./bookCoaching.ts";
import { whiteCoachingPack } from "../content/whiteCoachingPacks.ts";
import {
  ENGINE_CLAIM_LOSS,
  engineApproves,
  engineBestMoves,
  engineLoss,
  hasEngineLines,
} from "./engineLines.ts";

type BoardPiece = { square: string; type: string; color: string };
type Candidate = { san: string; why: string };
export type PositionGuide = BoundaryGuide & {
  priority: "check" | "material" | "recapture" | "center" | "king" | "development" | "plan";
  candidates: Candidate[];
  /** The square of a piece the opponent threatens to win, if any. */
  threatened?: string;
};

const NAMES: Record<string, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};
const VALUES: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 };
const STARTING: Record<string, string> = {
  b1: "n",
  g1: "n",
  c1: "b",
  f1: "b",
  b8: "n",
  g8: "n",
  c8: "b",
  f8: "b",
};

/** A knight or bishop still on its own color's starting square (a White bishop on f8 is not). */
function onHomeSquare(color: string | undefined, square: string, type: string | undefined) {
  const rank = color === "w" ? "1" : color === "b" ? "8" : square[1];
  return STARTING[square] === type && square[1] === rank;
}

function pieces(chess: Chess): BoardPiece[] {
  return chess.board().flatMap((rank, row) =>
    rank.flatMap((piece, file) =>
      piece
        ? [
            {
              ...piece,
              square: `${String.fromCharCode(97 + file)}${8 - row}`,
            },
          ]
        : []
    )
  );
}

/** "a, b and c". */
function joinList(items: string[]): string {
  return items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items.at(-1)}` : items[0] || "";
}

function openingPlan(openingId: string | null | undefined, chess: Chess): BoundaryGuide {
  const pawn = (sq: string, color: string) => {
    const p = chess.get(sq);
    return p?.type === "p" && p.color === color;
  };
  if (isBlackOpening(openingId)) {
    const plan = structurePlan(loadedBlackCourse(openingId)?.plans || [], chess);
    if (plan) return plan;
    // A break the board still allows: the pawn is behind the square and
    // nothing stands on it.
    const canBreak = (from: string[], to: string) =>
      from.some((sq) => pawn(sq, "b")) && !chess.get(to as never);
    const breaks = [
      canBreak(["c6", "c7"], "c5") ? "…c5" : "",
      canBreak(["e6", "e7"], "e5") ? "…e5" : "",
      canBreak(["d6", "d7"], "d5") ? "…d5" : "",
    ].filter(Boolean);
    return {
      goal: "Challenge White’s space with active pieces and a supported central break.",
      piece:
        "Find your least active piece, check the central pawn contacts, and coordinate a rook with a useful file.",
      next: breaks.length
        ? `Compare preparing ${breaks.join(" or ")} with improving a piece. Calculate the exchanges before changing the pawn structure.`
        : "Compare an open-file move, a useful bishop diagonal, and a knight improvement. Your original pawn break may no longer exist.",
      watch:
        "White may expand in the center or gain time by attacking a defender. Check its checks, captures, and pawn advances before a quiet move.",
    };
  }
  const pack = whiteCoachingPack(openingId);
  if (pack) {
    const plan = structurePlan(pack.plans, chess, "w") || pack.planFor?.(chess);
    if (plan) return plan;
  }
  return {
    goal: "Choose a plan from this pawn structure: improve the least active piece or prepare a supported central break.",
    piece: "Find an open file, a useful diagonal, or a central square your pieces can support.",
    next: "Compare a piece improvement with a central pawn move. Calculate the opponent's checks, captures, and threats before committing.",
    watch:
      "The pawn structure has changed, so a familiar opening maneuver may no longer fit. Recount attackers and defenders in the center.",
  };
}

function fileObservation(chess: Chess, color: "w" | "b" = "w"): string {
  const board = pieces(chess);
  const open: string[] = [];
  const halfOpen: string[] = [];
  for (const file of "abcdefgh") {
    const pawns = board.filter((piece) => piece.type === "p" && piece.square[0] === file);
    if (!pawns.length) open.push(file);
    else if (!pawns.some((piece) => piece.color === color)) halfOpen.push(file);
  }
  return [
    open.length ? `Open files: ${open.join(", ")}.` : "There is no fully open file yet.",
    halfOpen.length
      ? `Half-open files for ${color === "w" ? "White" : "Black"}: ${halfOpen.join(", ")}.`
      : "",
    "A rook needs an entry square as well as an open file.",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Whether the move takes a pawn away from in front of its own castled king. */
function breaksShield(chess: Chess, move: ChessMove): boolean {
  if (move.piece !== "p") return false;
  const king = pieces(chess).find((piece) => piece.type === "k" && piece.color === move.color);
  if (!king) return false;
  const kingFile = king.square[0];
  const wing = "fgh".includes(kingFile) ? "fgh" : "abc".includes(kingFile) ? "abc" : "";
  if (!wing || !wing.includes(move.from[0])) return false;
  const rank = Number(move.from[1]);
  return move.color === "w" ? rank <= 3 : rank >= 6;
}

/** A move written the way the coach writes it: Black's moves get "…". */
function written(move: { san: string; color?: string }): string {
  return move.color === "b" ? `…${move.san}` : move.san;
}

function sideName(color: "w" | "b"): string {
  return color === "w" ? "White" : "Black";
}

/**
 * What the opponent can win right now, or would win if it were their move:
 * the exchange is played out, so a defended piece attacked by a cheaper one
 * still counts, and an attacked piece that is defended enough does not.
 */
function threatTo(chess: Chess, color: "w" | "b"): Capture | null {
  return chess.turn() === color ? threatAgainstMover(chess) : bestCapture(chess);
}

export function threatObservation(chess: Chess, color: "w" | "b" = "w"): string {
  const opponent = sideName(color === "w" ? "b" : "w");
  const threat = threatTo(chess, color);
  if (threat?.move.captured) {
    return `${opponent} threatens ${written(threat.move)}, winning your ${NAMES[threat.move.captured]} on ${threat.move.to}. Deal with that before anything else.`;
  }
  const attacked = pieces(chess)
    .filter(
      (piece) =>
        piece.color === color &&
        piece.type !== "k" &&
        chess.isAttacked(piece.square, color === "w" ? "b" : "w")
    )
    .sort((a, b) => VALUES[b.type] - VALUES[a.type])
    .slice(0, 2);
  return attacked.length
    ? `${opponent} attacks your ${attacked.map((piece) => `${NAMES[piece.type]} on ${piece.square}`).join(" and ")}, but ${attacked.length === 1 ? "it is" : "they are"} defended well enough for now. Recount if a defender moves.`
    : "Check the opponent’s checks, captures and threats before your next move.";
}

/** Whether the side has claimed any of the center with a pawn yet. */
function hasCentralPawn(board: BoardPiece[], color: "w" | "b"): boolean {
  const home = color === "w" ? "2" : "7";
  return board.some(
    (piece) =>
      piece.color === color &&
      piece.type === "p" &&
      "cde".includes(piece.square[0]) &&
      piece.square[1] !== home
  );
}

/** The plan for the pawn structure on the board, from the student's side. */
export function structurePlanFor(
  openingId: string | null | undefined,
  chess: Chess
): BoundaryGuide {
  return openingPlan(openingId, chess);
}

const guideCache = new Map<string, PositionGuide>();

/**
 * Coaching for a position outside the course, from the student's side. Order
 * matters: a check, then material that can be won back or that is about to
 * be lost, then the opening priorities (center, development, castling), then
 * the plan for the pawn structure. Candidate moves never lose material to a
 * simple capture.
 */
export function positionGuideFor(
  openingId: string | null | undefined,
  moveNumber: number,
  chess: Chess
): PositionGuide {
  const last = chess.history({ verbose: true }).at(-1);
  // Engine scores arriving for the position change the guide.
  const key = `${openingId}|${moveNumber}|${chess.fen()}|${last?.san ?? ""}|${hasEngineLines(chess.fen())}`;
  const cached = guideCache.get(key);
  if (cached) return cached;
  const guide = buildPositionGuide(openingId, moveNumber, chess, last);
  if (guideCache.size > 200) guideCache.clear();
  guideCache.set(key, guide);
  return guide;
}

function buildPositionGuide(
  openingId: string | null | undefined,
  moveNumber: number,
  chess: Chess,
  last: { captured?: string; color?: string } | undefined
): PositionGuide {
  const color = isBlackOpening(openingId) ? "b" : "w";
  const opponent = color === "w" ? "b" : "w";
  const opponentName = sideName(opponent);
  const home = color === "w" ? "e1" : "e8";
  const board = pieces(chess);
  const king = board.find((p) => p.color === color && p.type === "k");
  const toMove = chess.turn() === color;
  const threat = threatTo(chess, color);
  // What the engine makes of an attacked piece, from the moves it approves:
  // all of them deal with it (a real threat), none of them (something else
  // matters more), or some of them (defending and playing on are both fine:
  // a poisoned pawn, an even trade, a counterattack). Said below.
  const fen0 = chess.fen();
  const approvedNow = toMove && hasEngineLines(fen0) ? engineBestMoves(fen0) : [];
  const answersThreat = (uci: string) => {
    if (!threat) return true;
    const after = new Chess(fen0);
    try {
      if (!after.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] }))
        return false;
    } catch {
      return false;
    }
    return bestCapture(after)?.move.to !== threat.move.to;
  };
  const answered = threat && approvedNow.length ? approvedNow.map(answersThreat) : null;
  const materialBalance = board.reduce(
    (total, piece) => total + (piece.color === color ? 1 : -1) * VALUES[piece.type],
    0
  );
  // A capture the engine refutes (a fork, a pin, a trapped piece) wins nothing.
  const fen = chess.fen();
  // With engine scores, captures it refutes are dropped and the rest are
  // listed best first; a single capture the coach says "wins" must be close
  // to the engine's best move (see below).
  // Among captures worth the same, one by a pawn that shields the king
  // (gxf3 with the king on g1) comes after the others, then the cheaper
  // capturing piece first.
  // Without the engine, a capture that hands the opponent a bigger capture
  // elsewhere is refuted too: cxd5 with the c4 pawn pinned to its queen
  // allows ...Bxe2.
  const opensBiggerLoss = (capture: { move: ChessMove; gain: number }) => {
    const after = new Chess(fen);
    after.move(capture.move.san);
    const reply = winningCaptures(after).find((r) => r.move.to !== capture.move.to);
    return Boolean(reply && reply.gain > capture.gain);
  };
  const captures = toMove
    ? winningCaptures(chess)
        .filter((capture) => {
          const verdict = engineApproves(fen, capture.move);
          return verdict === true || (verdict !== false && !opensBiggerLoss(capture));
        })
        .sort(
          (a, b) =>
            (engineLoss(fen, a.move) ?? 0) - (engineLoss(fen, b.move) ?? 0) ||
            b.gain - a.gain ||
            Number(breaksShield(chess, a.move)) - Number(breaksShield(chess, b.move)) ||
            VALUES[a.move.piece || "p"] - VALUES[b.move.piece || "p"]
        )
    : [];
  // Taking the attacking piece also answers its threat, so it comes first.
  const removesThreat = threat
    ? captures.find((capture) => capture.move.to === threat.move.from)
    : undefined;
  const gains = removesThreat
    ? [removesThreat, ...captures.filter((capture) => capture !== removesThreat)]
    : captures;
  const bestGain = gains[0] || null;
  // The capture the coach leads with must be close to the engine's best move
  // (taking the attacker only needs to be sound); others may follow it.
  const claimable = Boolean(
    bestGain &&
    (bestGain === removesThreat || engineApproves(fen, bestGain.move, ENGINE_CLAIM_LOSS) !== false)
  );
  const opponentJustCaptured = Boolean(last?.captured && last.color === opponent);
  const undeveloped = board.filter(
    (p) => p.color === color && onHomeSquare(p.color, p.square, p.type)
  );
  const castles = toMove ? chess.moves().filter((san) => /^O-O/.test(san)) : [];
  const structure = openingPlan(openingId, chess);
  let guide: Omit<PositionGuide, "candidates">;
  if (king && chess.isAttacked(king.square, opponent)) {
    guide = {
      priority: "check",
      goal: "Your king is in check. Deal with that threat before any opening plan.",
      piece: `Find a legal king move, capture of the checking piece, or block of the checking line to ${king.square}.`,
      next: "Compare the legal check evasions, then ask what the opponent can capture after each one.",
      watch:
        "A move that follows the usual opening setup cannot be played if it leaves the king in check.",
    };
  } else if (
    bestGain?.move.captured &&
    claimable &&
    (bestGain === removesThreat || bestGain.gain >= (threat?.gain ?? 0)) &&
    (opponentJustCaptured || materialBalance < 0 || bestGain.gain >= 2)
  ) {
    const takes = gains
      .slice(0, 3)
      .map(({ move }) => `${written(move)} takes the ${NAMES[move.captured || "p"]} on ${move.to}`);
    // A pawn the count says is free may be won back at once (dxc5 e6);
    // without the engine's view, say only the count.
    const unvettedPawn =
      bestGain !== removesThreat && bestGain.move.captured === "p" && !hasEngineLines(fen);
    guide = {
      priority: "recapture",
      goal:
        bestGain === removesThreat && threat
          ? `Your ${NAMES[threat.move.captured || "p"]} on ${threat.move.to} is attacked by the ${NAMES[threat.move.piece || "p"]} on ${threat.move.from}, and ${written(bestGain.move)} takes that ${NAMES[threat.move.piece || "p"]}.`
          : unvettedPawn
            ? `By the count on ${bestGain.move.to}, ${written(bestGain.move)} wins the pawn there. Check whether ${opponentName} wins it back at once before taking.`
            : materialBalance < 0
              ? `Win back material first: ${takes.join("; ")}.`
              : `${opponentName} has left material unprotected: ${takes.join("; ")}.`,
      piece:
        "Choose the capture that leaves your pieces active and your pawns healthy, and check that the capturing piece is safe afterward.",
      next: "Count the captures on that square to the end before taking; then return to development and the central plan.",
      watch:
        "A capture that loses the capturing piece wins nothing. Check what each capture leaves undefended.",
    };
  } else if (threat?.move.captured) {
    const target = threat.move.captured;
    const attacker = threat.move.piece || "p";
    const cheaper = VALUES[attacker] < VALUES[target];
    // A pawn the count on its square says is lost may be a gambit pawn or
    // win back with interest; without the engine's view, say only the count.
    const unvetted = target === "p" && !hasEngineLines(fen);
    guide = {
      priority: "material",
      goal: unvetted
        ? `Your pawn on ${threat.move.to} is attacked by the ${NAMES[attacker]} on ${threat.move.from}, and by the count on that square ${written(threat.move)} wins it. Check whether a recapture or a counterattack changes that before a quiet move.`
        : `Your ${NAMES[target]} on ${threat.move.to} is attacked by the ${NAMES[attacker]} on ${threat.move.from}: ${written(threat.move)} would win material. Save it before anything else.`,
      piece: cheaper
        ? `Defending the ${NAMES[target]} is not enough, because the ${NAMES[attacker]} is worth less. Move it to a safe square, or answer with a stronger threat.`
        : `Move the ${NAMES[target]} to a safe square, defend it, or answer with a stronger threat.`,
      next: "Once it is safe, return to development and the central plan.",
      watch:
        "Before every move, check what each enemy piece attacks and whether your pieces are defended enough.",
    };
  } else if (bestGain?.move.captured && claimable) {
    guide = {
      priority: "recapture",
      goal: `${written(bestGain.move)} wins the ${NAMES[bestGain.move.captured]} on ${bestGain.move.to}.`,
      piece:
        "Check that the capturing piece is safe afterward and that nothing else is left undefended.",
      next: "Count the captures on that square to the end before taking; then return to development and the central plan.",
      watch: "A free pawn is only free if taking it does not lose time you need for development.",
    };
  } else if (moveNumber <= 3 && !hasCentralPawn(board, color)) {
    guide = {
      priority: "center",
      goal: "Put a pawn in the center first: it gives your knights and bishops good squares.",
      piece: `A ${color === "w" ? "d- or e-pawn" : "c-, d- or e-pawn"} move claims central space and opens a line for a bishop.`,
      next: "Then develop the knights toward the center and get ready to castle.",
      watch: "Flank pawn moves and early queen trips spend time without helping the center.",
    };
  } else if (castles.length) {
    guide = {
      priority: "king",
      goal: `Castling is available: ${castles.join(" or ")} gets your king out of the center.`,
      piece: "Castling also brings a rook toward the center files.",
      next: "After castling, finish development and choose a central plan.",
      watch: `Central exchanges can open lines toward a king left on ${home}.`,
    };
  } else if (undeveloped.length) {
    const list = undeveloped.map((p) => `${NAMES[p.type]} on ${p.square}`);
    const first = undeveloped[0];
    const stuck = !chess.moves({ verbose: true, square: first.square }).length && toMove;
    guide = {
      priority: "development",
      goal: `Bring your remaining undeveloped pieces into play: ${list.length > 1 ? `${list.slice(0, -1).join(", ")} and ${list.at(-1)}` : list[0]}.`,
      piece: stuck
        ? `The ${NAMES[first.type]} on ${first.square} has no legal move yet: first move the pawn or piece that blocks it.`
        : `Choose a useful square for the ${NAMES[first.type]} on ${first.square} that helps the center and is not an easy target.`,
      next: "Compare developing a new piece with any urgent response to central pressure. A pawn preparation needs a concrete payoff to justify delaying development.",
      watch: `${structure.watch} Repeating a book route only helps if its squares are available.`,
    };
  } else if (king?.square === home) {
    guide = {
      priority: "king",
      goal: "Find a safe home for your king before opening the center.",
      piece:
        "Inspect what still blocks castling, whether the castling rights remain, and which squares the opponent controls near the king.",
      next: "If castling rights remain, clear the path while preserving the center. If they are lost, find a safe king square and connect the rooks.",
      watch: `Central exchanges can open checks against the king on ${home}. Development must address that exposure.`,
    };
  } else {
    guide = { priority: "plan", ...structure };
  }
  const candidates = candidateMoves(chess, guide.priority, threat, moveNumber >= 10 ? 6 : 3, color);
  // An attacked piece the engine's best move leaves where it is: something
  // else matters more (a stronger threat, a counterattack, a gambit pawn).
  // Only when none of the moves the engine approves deals with the threat:
  // engine moves close in value often include one that defends.
  if (guide.priority === "material" && threat && answered && toMove) {
    const stillLost = !answered.some(Boolean);
    const mixed = answered.some(Boolean) && !answered.every(Boolean);
    const target = NAMES[threat.move.captured || "p"];
    if (mixed) {
      // Both are fine by the engine: the capture is no simple win of material.
      const uciOf = (san: string) => {
        const move = chess.moves({ verbose: true }).find((m) => m.san === san);
        return move ? `${move.from}${move.to}${move.promotion || ""}` : "";
      };
      const defends = candidates.filter((c) => answersThreat(uciOf(c.san))).map((c) => c.san);
      const ignores = candidates.filter((c) => !answersThreat(uciOf(c.san))).map((c) => c.san);
      guide = {
        ...guide,
        goal: `Your ${target} on ${threat.move.to} is attacked by the ${NAMES[threat.move.piece || "p"]} on ${threat.move.from}. Decide whether to deal with that${defends.length ? ` (${joinList(defends)})` : ""} or to leave it for now${ignores.length ? ` (${joinList(ignores)})` : ""}: the engine accepts both.`,
        piece: `Work out what ${written(threat.move)} would really win once you have a reply ready: a recapture, a counter-threat, or a pawn that costs more than it is worth.`,
        next: "Then return to development and the central plan.",
      };
    } else if (stillLost) {
      guide = {
        ...guide,
        goal: `Your ${target} on ${threat.move.to} is attacked by the ${NAMES[threat.move.piece || "p"]} on ${threat.move.from}, but the strongest moves here do something else first.`,
        piece: `The engine prefers ${joinList(candidates.map((c) => c.san))}. Work out why the ${target} can wait: a bigger threat to meet, or a way to win it back.`,
        next: "Once that is settled, return to development and the central plan.",
      };
    }
  }
  // When the engine has ruled out every move that serves the opening priority
  // (each developing move, say, runs into a tactic), the goal says so instead
  // of pointing at moves the candidates no longer include.
  const legal = toMove ? chess.moves({ verbose: true }) : [];
  const serves = (san: string) => {
    const move = legal.find((m) => m.san === san);
    if (!move) return false;
    if (guide.priority === "king") return /^O-O/.test(move.san);
    if (guide.priority === "development") return onHomeSquare(move.color, move.from, move.piece);
    if (guide.priority === "center") return move.piece === "p" && "cde".includes(move.to[0]);
    return true;
  };
  if (
    hasEngineLines(fen) &&
    toMove &&
    ["center", "king", "development"].includes(guide.priority) &&
    candidates.length &&
    !candidates.some((candidate) => serves(candidate.san))
  ) {
    const usual =
      guide.priority === "king"
        ? "castling"
        : guide.priority === "center"
          ? "central pawn"
          : "developing";
    // The engine may see a threat or a stronger chance of your own: say
    // only what is known.
    guide = {
      ...guide,
      next:
        guide.priority === "king"
          ? "Castle once it no longer costs anything; then finish development and choose a central plan."
          : guide.priority === "center"
            ? "Claim the center once it is safe to; then develop the knights."
            : "Bring the remaining pieces out once it is safe to.",
      goal: `The usual ${usual} move is not the best here: the engine prefers ${joinList(candidates.map((c) => c.san))}.`,
      piece: `Work out what ${candidates.length > 1 ? "those moves do" : "that move does"} that a ${usual} move would not: a threat to meet, or a chance to take.`,
    };
  }
  const urgent =
    guide.priority === "check" || guide.priority === "material" || guide.priority === "recapture";
  const depth =
    moveNumber >= 10
      ? {
          strategy: `${guide.goal} ${urgent ? "" : `${structure.next} `}${fileObservation(chess, color)}`,
          tradeoff:
            "Compare what each candidate gains with the defenders it removes, the squares its pawn move leaves, and the opponent’s most forcing reply. A legal move and a good strategic story alone do not establish tactical safety.",
          blackPlan: `${structure.watch} ${threatObservation(chess, color)}`,
          takeaway: urgent
            ? "Resolve the concrete threat first; then rebuild your plan from the resulting position."
            : "Compare a pawn break, an open-file move and a piece improvement; calculate the opponent’s reply to each.",
        }
      : undefined;
  return {
    ...guide,
    depth,
    threatened: threat?.move.to,
    candidates,
  };
}

function candidateMoves(
  chess: Chess,
  priority: PositionGuide["priority"],
  threat: Capture | null,
  limit = 3,
  color: "w" | "b" = "w"
): Candidate[] {
  if (chess.turn() !== color || chess.isGameOver()) return [];
  const opponent = color === "w" ? "b" : "w";
  // First pass: a cheap opening-principles score for every legal move.
  const scored = chess.moves({ verbose: true }).map((move) => {
    const castle = /^O-O/.test(move.san);
    const develops = onHomeSquare(move.color, move.from, move.piece);
    const teachingSquare = color === "w" ? move.to : `${move.to[0]}${9 - Number(move.to[1])}`;
    const central = move.piece === "p" && ["e4", "d4", "e3", "d3", "c4"].includes(teachingSquare);
    let score = castle ? 8 : develops ? 6 : central ? 4 : 0;
    if (priority === "king" && castle) score += 6;
    if (priority === "center" && central) score += 8;
    if (move.piece === "n") {
      if (["c3", "f3", "d4", "e4", "d5", "e5"].includes(teachingSquare)) score += 3;
      if (["d2", "e2"].includes(teachingSquare)) score += 2;
      if (["a", "h"].includes(move.to[0])) score -= 3;
    }
    if (
      move.piece === "b" &&
      ["c4", "d3", "b5", "g5", "f4", "e3", "b2", "g2"].includes(teachingSquare)
    )
      score += 2;
    if (move.piece === "q" && fullMoveNumber(chess) <= 8 && !move.captured) score -= 4;
    if (move.piece === "r" && !chess.get(`${move.to[0]}${color === "w" ? "2" : "7"}`)) score += 1;
    if (move.captured) score += 3;
    if (chess.isAttacked(move.to, opponent) && !move.captured && !castle) score -= 2;
    if (move.san.endsWith("#")) score += 1000;
    return { move, score };
  });
  // Second pass, on the moves worth showing: material won minus the best
  // capture the opponent then has, and no mate in one allowed.
  const shortlist = [...scored]
    .sort((a, b) => b.score - a.score)
    .filter(
      (entry, index) =>
        // Under attack, any move may be the answer (a block, a counterattack).
        priority === "material" ||
        priority === "check" ||
        index < 12 ||
        entry.move.captured ||
        /^O-O/.test(entry.move.san)
    );
  const safety = moveSafety(
    chess,
    shortlist.map((entry) => entry.move)
  );
  const judged = shortlist
    .map((entry) => {
      const { net, allowsMate } = safety.get(entry.move) ?? { net: -100, allowsMate: true };
      let score = entry.score + net * 12;
      if (priority === "recapture" && entry.move.captured && net > 0) score += 20 + net * 2;
      if (priority === "material" && threat && net >= 0 && entry.move.from === threat.move.to)
        score += 6;
      return { move: entry.move, score, net, allowsMate };
    })
    .filter((entry) => !entry.allowsMate);
  const fen = chess.fen();
  const describe = ({ move }: { move: ChessMove }) => ({
    san: move.san,
    why: describeCandidate(move, priority, threat?.move.to),
  });
  if (hasEngineLines(fen)) {
    // The engine judges what the static checks cannot see (forks, trapped
    // pieces, counterattacks): keep the moves it approves, in teaching
    // order, and add its own best moves if fewer than two remain.
    const approved = judged
      .filter((entry) => engineApproves(fen, entry.move) === true)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((entry) => entry.move);
    const legal = chess.moves({ verbose: true });
    for (const uci of engineBestMoves(fen)) {
      if (approved.length >= Math.min(limit, 2)) break;
      const move = legal.find((m) => `${m.from}${m.to}${m.promotion || ""}` === uci);
      if (move && !approved.some((m) => m.san === move.san)) approved.push(move);
    }
    return approved.map((move) => describe({ move }));
  }
  // A candidate must not simply lose material; if every move does, show the
  // least costly ones.
  const safe = judged.filter((entry) => entry.net >= 0);
  // Legal candidates for comparison only. Do not call this an engine ranking
  // or recommend a familiar move solely because its SAN matches a main line.
  return (safe.length ? safe : judged)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(describe);
}

/**
 * A reply for the side to move chosen from the board alone, for when the
 * engine does not answer: the opening priorities, with moves that lose
 * material or allow mate left out. Null only when there is no legal move.
 */
export function fallbackReply(chess: Chess): string | null {
  if (chess.isGameOver()) return null;
  const color = chess.turn();
  const threat = threatTo(chess, color);
  const priority: PositionGuide["priority"] = chess.inCheck()
    ? "check"
    : threat?.move.captured
      ? "material"
      : bestCapture(chess)
        ? "recapture"
        : "development";
  const [best] = candidateMoves(chess, priority, threat, 1, color);
  return best?.san ?? chess.moves()[0] ?? null;
}

function fullMoveNumber(chess: Chess): number {
  return Number(chess.fen().split(" ")[5]) || 1;
}

export function describeCandidate(
  move: ChessMove,
  priority: PositionGuide["priority"],
  threatenedSquare?: string
): string {
  if (priority === "check") return "answers the check; compare the resulting threats";
  if (/^O-O/.test(move.san)) return "moves the king out of the center and activates a rook";
  if (move.flags?.includes("c") || move.flags?.includes("e"))
    return `captures ${move.captured ? `the ${NAMES[move.captured]}` : "material"} on ${move.to}; calculate any further recaptures`;
  if (threatenedSquare && move.from === threatenedSquare)
    return `moves the attacked ${NAMES[move.piece || "n"]} to safety on ${move.to}`;
  if (onHomeSquare(move.color, move.from, move.piece))
    return `develops the ${NAMES[move.piece || "n"]} from ${move.from} toward ${move.to}`;
  if (move.piece === "p")
    return `changes the pawn structure and control around ${move.to}; check the squares left behind`;
  return `relocates the ${NAMES[move.piece || "n"]} from ${move.from} to ${move.to}; inspect the squares it controls and the defender it leaves behind`;
}

/**
 * The material consequence of a move, in words, or "" when it neither gives
 * up nor allows the loss of material. True whether or not the opponent has
 * already answered, so it can stay on screen after the reply.
 */
export function moveConsequence(
  fenBefore: string,
  move: ChessMove,
  engineLoss: number | null = null
): string {
  // A move the engine approves does not lose material, whatever a static
  // count of the next capture suggests (a counterattack, a regained pawn).
  if (engineApproves(fenBefore, move) === true) return "";
  if (engineLoss != null && engineLoss < 100) return "";
  const before = new Chess(fenBefore);
  if (move.captured) {
    const gain = captureGain(before, move);
    if (gain < 0)
      return `It gives up the ${NAMES[move.piece || "p"]} for ${move.captured === "p" ? "a pawn" : `a ${NAMES[move.captured]}`}.`;
  }
  const after = new Chess(fenBefore);
  after.move(move.san);
  if (hasMateInOne(after)) return "It allows checkmate in one move.";
  // Taking back on the square of a capture is part of that capture's
  // exchange, already counted above: an even trade loses nothing.
  const reply = move.captured
    ? winningCaptures(after).find((capture) => capture.move.to !== move.to)
    : bestCapture(after);
  if (!reply?.move.captured) return "";
  const yours =
    reply.move.to === move.to
      ? `the ${NAMES[move.piece || "p"]} you just moved`
      : `your ${NAMES[reply.move.captured]} on ${reply.move.to}`;
  // When you can take back, the capture starts an exchange: say what the
  // exchange costs, not that the piece is simply lost.
  if (reply.gain < (VALUES[reply.move.captured] ?? 0))
    return `It allows ${written(reply.move)}, and the exchanges on ${reply.move.to} cost you ${reply.gain === 1 ? "a pawn" : `${reply.gain} pawns' worth of material`}.`;
  return `It allows ${written(reply.move)}, which wins ${yours}.`;
}

export function explainOffBookMove(
  fenBefore: string,
  move: ChessMove,
  concise = false,
  engineLoss: number | null = null
): string {
  const before = new Chess(fenBefore);
  let effect: string;
  if (/^O-O/.test(move.san))
    effect = `${move.san} makes the king safer and brings a rook into play.`;
  else if (move.flags?.includes("c") || move.flags?.includes("e")) {
    const captured = before.get(move.to);
    effect = `${move.san} captures ${captured ? `a ${NAMES[captured.type]}` : "a pawn"} and changes the local tension. Check the recapture before judging the exchange.`;
  } else if (onHomeSquare(move.color, move.from, move.piece)) {
    effect = `${move.san} brings the ${NAMES[move.piece || "n"]} from ${move.from} into play on ${move.to}. Its value depends on central control and whether it can stay active there.`;
  } else if (move.piece === "p") {
    effect = `${move.san} changes pawn control around ${move.to} and leaves squares behind. Ask which piece or pawn break benefits from that change.`;
  } else {
    const waiting = pieces(before).some(
      (piece) => piece.color === move.color && onHomeSquare(piece.color, piece.square, piece.type)
    );
    effect = `${move.san} relocates the ${NAMES[move.piece || "n"]} from ${move.from} to ${move.to}. ${
      waiting
        ? "Compare its new job with developing a piece that has not joined the game."
        : "Compare its new job with the one it had on its old square."
    }`;
  }
  const consequence = moveConsequence(fenBefore, move, engineLoss);
  return [concise ? effect.split(/\.\s|\.$/)[0] + "." : effect, consequence]
    .filter(Boolean)
    .join(" ");
}
